import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Bed, Award, Dumbbell, UtensilsCrossed, Car } from 'lucide-react';
import { BookingWidget } from '../components/BookingWidget';
import { Button } from '../components/ui/button';
import { heroImages, whyChooseUs, roomCategories, hotelInfo } from '../mock';

const iconMap = {
  MapPin,
  Bed,
  Award,
  Dumbbell,
  UtensilsCrossed,
  Car
};

export const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel - Updated Design */}
      <section className="relative" style={{ height: 'calc(100vh + 100px)' }}>
        {/* Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Hotel view ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all"
        >
          <ChevronLeft size={24} className="text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all"
        >
          <ChevronRight size={24} className="text-white" />
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-[180px] left-1/2 -translate-x-1/2 z-10 flex space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>

        {/* Hero Content - Repositioned */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Text Content - Upper-middle area with generous top padding */}
          <div className="flex-1 flex items-center justify-center px-4" style={{ paddingTop: '140px', paddingBottom: '180px' }}>
            <div className="text-center max-w-4xl">
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight leading-tight">
                {hotelInfo.tagline}
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl text-white/95 font-light">
                Experience the perfect blend of luxury accommodations, fine dining, and wellness
              </p>
            </div>
          </div>
        </div>

        {/* Booking Widget - Positioned at 80% height with overlap */}
        <div className="absolute left-0 right-0 z-20" style={{ top: '80%' }}>
          <div className="container mx-auto px-4">
            <BookingWidget variant="hero" />
          </div>
        </div>
      </section>

      {/* Add spacing to prevent content overlap */}
      <div className="h-32" />

      {/* Service Overview Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#102854' }}>
              Discover Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every aspect of your stay has been thoughtfully designed to ensure comfort, luxury, and memorable experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Accommodation */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <img
                  src={roomCategories[0].images[0]}
                  alt="Accommodation"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#102854' }}>
                  Accommodation
                </h3>
                <p className="text-gray-600 mb-4">
                  Choose from our range of elegantly appointed rooms and suites, each designed to be your perfect home away from home.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="w-full font-semibold transition-all hover:scale-105"
                  style={{
                    borderColor: '#C9A961',
                    color: '#102854'
                  }}
                >
                  <Link to="/accommodation">Explore Rooms</Link>
                </Button>
              </div>
            </div>

            {/* Food & Beverage */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80"
                  alt="Dining"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#102854' }}>
                  Food & Beverage
                </h3>
                <p className="text-gray-600 mb-4">
                  Savor exquisite culinary creations prepared by our expert chefs, from breakfast to fine dining and cocktails.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="w-full font-semibold transition-all hover:scale-105"
                  style={{
                    borderColor: '#C9A961',
                    color: '#102854'
                  }}
                >
                  <Link to="/dining">View Menu</Link>
                </Button>
              </div>
            </div>

            {/* Health Club & Wellness */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80"
                  alt="Wellness"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3" style={{ color: '#102854' }}>
                  Health Club & Wellness
                </h3>
                <p className="text-gray-600 mb-4">
                  Rejuvenate your body and mind with our state-of-the-art fitness center, spa, sauna, and steam facilities.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="w-full font-semibold transition-all hover:scale-105"
                  style={{
                    borderColor: '#C9A961',
                    color: '#102854'
                  }}
                >
                  <Link to="/wellness">Discover Wellness</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#102854' }}>
              Why Choose Pauline Hotels
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We go beyond accommodation to create unforgettable experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className="p-6 rounded-xl border-2 border-gray-100 hover:border-opacity-100 transition-all duration-300 hover:shadow-lg"
                  style={{ borderColor: '#f3f4f6' }}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: '#FEF3E2' }}
                  >
                    <Icon size={28} style={{ color: '#C9A961' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#102854' }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4" style={{ backgroundColor: '#102854' }}>
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Experience Luxury?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book your stay today and discover why guests choose Pauline Hotels for their comfort and elegance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="font-semibold text-base px-8 py-6 transition-all hover:scale-105"
              style={{
                backgroundColor: '#C9A961',
                color: '#ffffff'
              }}
            >
              <a href="/#booking">Book Now</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="font-semibold text-base px-8 py-6 bg-white transition-all hover:scale-105"
              style={{
                borderColor: '#C9A961',
                color: '#102854'
              }}
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};